import React, { useState, useEffect } from 'react';
import parse from 'html-react-parser';
import './WorldInboxEmail.css';
import { database } from '../firebaseConfig';
import { ref, get } from 'firebase/database';
import EmailAiPopup from './EmailAiPopup';
import EmailSentNotifier from './EmailSentNotifier';

// Firestore für /db-Pfad verwenden
import { firestore } from '../firebaseConfig';
import { collection, query as firestoreQuery, where, getDocs, updateDoc } from 'firebase/firestore';

const WorldInboxEmail = ({ email, userId, onDelete, onMarkAsRead }) => {
  const [expanded, setExpanded] = useState(false);
  const [emailContent, setEmailContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRead, setIsRead] = useState(() => {
    const initialIsRead = typeof email.isRead === 'boolean' ? email.isRead : false;
    console.log(`Initialisiere E-Mail "${email.subject}" mit isRead=${initialIsRead}`, email);
    return initialIsRead;
  });
  const [leadUpdated, setLeadUpdated] = useState(false);
  const [generatingReply, setGeneratingReply] = useState(false);
  const [aiReply, setAiReply] = useState(null);
  const [showEmailAiPopup, setShowEmailAiPopup] = useState(false);
  const [emailReplyData, setEmailReplyData] = useState(null);
  const [hasReplied, setHasReplied] = useState(false);
  const [emailNotifier, setEmailNotifier] = useState({
    status: '',
    message: '',
    scheduledTime: null,
    recipientEmail: null
  });

  // Formatiere das Datum für bessere Lesbarkeit
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      
      // Formatiere die Uhrzeit (HH:MM)
      const time = date.toLocaleTimeString('de-DE', { 
        hour: '2-digit', 
        minute: '2-digit'
      });
      
      // Formatiere das Datum (DD.MM.YYYY)
      const formattedDate = date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      
      return { time, date: formattedDate };
    } catch (err) {
      console.error('Fehler beim Formatieren des Datums:', err);
      return { time: '', date: dateString };
    }
  };

  // Extrahiere den Namen und die E-Mail-Adresse aus dem Absender
  const parseFromField = (fromString) => {
    try {
      // Versuche, Name und E-Mail-Adresse zu extrahieren
      const match = fromString.match(/^(.*?)\s*<(.+?)>$/);
      if (match) {
        return {
          name: match[1].trim(),
          email: match[2].trim()
        };
      }
      
      // Wenn kein Match, gib den gesamten String als E-Mail zurück
      return {
        name: fromString,
        email: ''
      };
    } catch (err) {
      console.error('Fehler beim Parsen des Absenders:', err);
      return { name: fromString, email: '' };
    }
  };

  const { time, date } = formatDate(email.date);
  const sender = parseFromField(email.from);

  // Zeige an, von welchem Konto die E-Mail empfangen wurde
  const receivedAtAccount = email.accountEmail || 'Unbekanntes Konto';

  // Verbesserte Funktion zum Aktualisieren aller Kontakte mit gleicher E-Mail
  const updateLeadReplyStatus = async (senderEmail) => {
    if (!userId || !senderEmail) return;

    try {
      const normalizedEmail = senderEmail.toLowerCase().trim();
      console.log(`🔥 Firestore-Suche nach Kontakten mit E-Mail: ${normalizedEmail}`);

      const contactsRef = collection(firestore, `World/${userId}/db`);
      const emailQuery = firestoreQuery(contactsRef, where('lead_email', '==', normalizedEmail));
      const querySnapshot = await getDocs(emailQuery);

      console.log(`Firestore Result Count: ${querySnapshot.size}`);

      if (!querySnapshot.empty) {
        let updatedCount = 0;

        for (const docSnap of querySnapshot.docs) {
          const contactData = docSnap.data();

          if (contactData.lead_has_replied) {
            console.log(`Kontakt ${docSnap.id} hat bereits geantwortet – überspringe`);
            continue;
          }

          console.log(`Aktualisiere Kontakt ${docSnap.id} …`);

          try {
            await updateDoc(docSnap.ref, {
              lead_has_replied: true,
              lead_reply_timestamp: Date.now(),
              lead_reply_date: new Date().toISOString(),
            });

            updatedCount++;
          } catch (updateErr) {
            console.error(`Fehler beim Aktualisieren von Kontakt ${docSnap.id}:`, updateErr);
          }
        }

        if (updatedCount > 0) {
          setLeadUpdated(true);
          console.log(`${updatedCount} Kontakte erfolgreich aktualisiert`);
        } else {
          console.log('Keine Kontakte aktualisiert');
        }
      } else {
        console.log(`Keine Kontakte mit der E-Mail ${normalizedEmail} gefunden – starte alternative Suche`);
        await alternativeSearchMethod(normalizedEmail);
      }
    } catch (err) {
      console.error('Fehler bei der Firestore-Kontaktsuche:', err);

      try {
        await alternativeSearchMethod(senderEmail.toLowerCase().trim());
      } catch (fallbackErr) {
        console.error('Alternative Suchmethode fehlgeschlagen:', fallbackErr);
      }
    }
  };

  // Alternative Suchmethode als Fallback
  const alternativeSearchMethod = async (email) => {
    console.log(`🔍 Alternative Firestore-Suche für ${email}`);

    try {
      const contactsRef = collection(firestore, `World/${userId}/db`);
      const querySnapshot = await getDocs(contactsRef);

      if (querySnapshot.empty) {
        console.log('Keine Kontakte in Firestore gefunden');
        return;
      }

      let updatedCount = 0;

      for (const docSnap of querySnapshot.docs) {
        const contactData = docSnap.data();

        if (!contactData.lead_email) continue;

        const contactEmail = contactData.lead_email.toLowerCase().trim();
        if (contactEmail === email || contactEmail.includes(email) || email.includes(contactEmail)) {
          if (contactData.lead_has_replied) {
            console.log(`Kontakt ${docSnap.id} hat bereits geantwortet – überspringe`);
            continue;
          }

          console.log(`Aktualisiere Kontakt ${docSnap.id} (${contactData.lead_email}) …`);

          try {
            await updateDoc(docSnap.ref, {
              lead_has_replied: true,
              lead_reply_timestamp: Date.now(),
              lead_reply_date: new Date().toISOString(),
            });

            updatedCount++;
          } catch (updateErr) {
            console.error(`Fehler beim Aktualisieren von Kontakt ${docSnap.id}:`, updateErr);
          }
        }
      }

      if (updatedCount > 0) {
        setLeadUpdated(true);
        console.log(`Alternative Suche: ${updatedCount} Kontakte aktualisiert`);
      } else {
        console.log('Keine Kontakte durch alternative Suche aktualisiert');
      }
    } catch (err) {
      console.error('Fehler bei der alternativen Firestore-Suche:', err);
    }
  };

  // Aktualisiere den isRead-Status, wenn sich die E-Mail-Eigenschaft ändert
  useEffect(() => {
    const newIsRead = typeof email.isRead === 'boolean' ? email.isRead : false;
    console.log(`E-Mail "${email.subject}" - Status aktualisiert:`, {
      alt: isRead,
      neu: newIsRead,
      emailProp: email.isRead,
      emailObj: email
    });
    
    if (isRead !== newIsRead) {
      setIsRead(newIsRead);
    }
  }, [email, email.isRead, email.subject, isRead]);

  // Prüfe beim Laden, ob diese E-Mail bereits beantwortet wurde
  useEffect(() => {
    const checkIfReplied = async () => {
      try {
        // Prüfe in der Datenbank, ob es einen Eintrag für diese E-Mail-ID gibt
        const repliesRef = ref(database, `/World/${userId}/emailReplies/${email.uid}`);
        const snapshot = await get(repliesRef);
        
        if (snapshot.exists()) {
          setHasReplied(true);
        }
      } catch (error) {
        console.error("Fehler beim Prüfen des Antwort-Status:", error);
      }
    };
    
    if (email && email.uid) {
      checkIfReplied();
    }
  }, [email, userId]);

  const toggleExpand = async () => {
    const wasExpanded = expanded;
    setExpanded(!wasExpanded);
    
    // Nur als gelesen markieren, wenn die E-Mail noch nicht als gelesen markiert ist
    if (!wasExpanded && !isRead) {
      console.log(`Markiere E-Mail "${email.subject}" mit ID ${email.uid} als gelesen (vorher: ${isRead})`);
      setIsRead(true);
      if (onMarkAsRead) {
        await onMarkAsRead(email);
      }
      
      // Prüfe, ob der Absender in der Kontaktdatenbank vorhanden ist
      if (sender.email) {
        await updateLeadReplyStatus(sender.email);
      }
    }
    
    // Lade den Inhalt, wenn die E-Mail geöffnet wird und noch nicht geladen wurde
    if (!wasExpanded && !emailContent && !loading) {
      fetchEmailContent();
    }
  };

  const fetchEmailContent = async () => {
    if (!userId) return;
    setLoading(true);
    
    try {
      const response = await fetch(
        `https://us-central1-bonusclub-ab5e2.cloudfunctions.net/fetchEmailContent?uid=${userId}&messageUid=${email.uid}`
      );
      if (!response.ok) {
        throw new Error('Fehler beim Laden des E-Mail-Inhalts');
      }
      const data = await response.json();
      console.log('Abgerufene E-Mail-Daten:', data); // Debugging-Log
      setEmailContent(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatEmailContent = (emailContent) => {
    try {
      // Wenn HTML-Inhalt verfügbar ist, verwende diesen
      if (emailContent.htmlContent) {
        return (
          <div className="email-content-html">
            {parse(emailContent.htmlContent, {
              replace: (domNode) => {
                // Entferne potenziell gefährliche Elemente
                if (domNode.name === 'script' || domNode.name === 'iframe') {
                  return <></>;
                }
                
                // Entferne externe Ressourcen (optional)
                if (domNode.name === 'img' && domNode.attribs && domNode.attribs.src) {
                  // Entferne externe Bilder oder ersetze sie durch Platzhalter
                  if (domNode.attribs.src.startsWith('http')) {
                    return (
                      <div className="image-placeholder">
                        [Externes Bild: {domNode.attribs.alt || 'Kein Alternativtext'}]
                      </div>
                    );
                  }
                }
                
                // Andere Elemente unverändert lassen
                return undefined;
              }
            })}
          </div>
        );
      }
      
      // Wenn nur Text verfügbar ist
      if (emailContent.textContent) {
        // Einfache Formatierung für Text
        const formattedText = emailContent.textContent
          .replace(/\r\n/g, '\n')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
        
        return (
          <div className="email-content-text">
            <pre style={{ 
              whiteSpace: 'pre-wrap', 
              wordBreak: 'break-word',
              fontFamily: 'inherit',
              fontSize: 'inherit',
              margin: 0,
              padding: 0
            }}>
              {formattedText}
            </pre>
          </div>
        );
      }
      
      // Fallback für Rohdaten
      if (emailContent.rawContent) {
        return (
          <div className="email-content-text">
            <pre style={{ 
              whiteSpace: 'pre-wrap', 
              wordBreak: 'break-word',
              fontFamily: 'inherit',
              fontSize: 'inherit',
              margin: 0,
              padding: 0
            }}>
              {emailContent.rawContent}
            </pre>
          </div>
        );
      }
      
      return <p>Keine Inhalte verfügbar</p>;
    } catch (err) {
      console.error('Fehler beim Verarbeiten des E-Mail-Inhalts:', err);
      return (
        <div className="email-content-error">
          <p>Fehler beim Anzeigen des Inhalts: {err.message}</p>
        </div>
      );
    }
  };

  const getReadStatusText = () => {
    return isRead ? 'Gelesen' : 'Ungelesen';
  };

  const handleReplyWithAi = () => {
    if (!emailContent) return;
    
    // Erstelle ein Objekt mit den Daten, die EmailAiPopup benötigt
    const replyData = {
      id: email.uid, // Verwende die E-Mail-UID als ID
      business_name: sender.name,
      lead_email: sender.email,
      lead_description: emailContent.textContent || emailContent.htmlContent || "",
      // Füge weitere Felder hinzu, die EmailAiPopup erwartet
      lead_website: "",
      lead_instagram: "",
      lead_tiktok: "",
      lead_type: "email_reply",
      // Flag für echte Reply-Erkennung (nicht Forward)
      isForwardOperation: false,
      // Speichere die ursprüngliche E-Mail für den Kontext
      original_email: {
        subject: email.subject,
        content: emailContent.textContent || emailContent.htmlContent || "",
        from: email.from,
        date: email.date
      },
      // Füge die Konto-Informationen hinzu
      accountEmail: email.accountEmail,
      accountId: email.accountId
    };
    
    setEmailReplyData(replyData);
    setShowEmailAiPopup(true);
  };

  // Füge einen Callback für das Schließen des EmailAiPopup hinzu
  const handleEmailPopupClose = async (emailSent = false) => {
    setShowEmailAiPopup(false);
    
    // Wenn eine E-Mail gesendet wurde, zeige den Notifier
    if (emailSent) {
      setEmailNotifier({
        status: 'success',
        message: 'E-Mail sent successfully!',
        scheduledTime: null,
        recipientEmail: null
      });
      
      // Notifier nach 5 Sekunden automatisch ausblenden
      setTimeout(() => {
        resetEmailNotifier();
      }, 5000);
    }
    
    // Prüfe, ob die E-Mail als beantwortet markiert wurde
    try {
      const repliesRef = ref(database, `/World/${userId}/emailReplies/${email.uid}`);
      const snapshot = await get(repliesRef);
      
      if (snapshot.exists()) {
        setHasReplied(true);
      }
    } catch (error) {
      console.error("Fehler beim Prüfen des Antwort-Status:", error);
    }
  };

  // Funktion zum Zurücksetzen des Notifiers
  const resetEmailNotifier = () => {
    setEmailNotifier({ status: '', message: '', scheduledTime: null, recipientEmail: null });
  };

  // Funktion zum Behandeln des E-Mail-Ergebnisses - direkt vom EmailAiPopup aufgerufen
  const handleEmailResult = (status, message, rowData, scheduledTime, recipientEmail) => {
    console.log(`E-Mail-Ergebnis empfangen: ${status} - ${message} ${scheduledTime ? '- Geplant für: ' + new Date(scheduledTime).toLocaleString() : ''} - Empfänger: ${recipientEmail || 'nicht angegeben'}`);
    console.log("scheduledTime in handleEmailResult:", scheduledTime, "Typ:", typeof scheduledTime);
    console.log("recipientEmail in handleEmailResult:", recipientEmail);
    
    // Stelle sicher, dass scheduledTime ein Zeitstempel ist
    let validScheduledTime = scheduledTime;
    if (scheduledTime && typeof scheduledTime === 'string') {
      try {
        validScheduledTime = new Date(scheduledTime).getTime();
        if (isNaN(validScheduledTime)) {
          console.error("Ungültiger Zeitstempel:", scheduledTime);
          validScheduledTime = null;
        }
      } catch (e) {
        console.error("Fehler beim Konvertieren des Zeitstempels:", e);
        validScheduledTime = null;
      }
    }
    
    setEmailNotifier({ 
      status, 
      message, 
      scheduledTime: validScheduledTime,
      recipientEmail: recipientEmail || (rowData ? rowData.lead_email : null)
    });
    
    // Automatisch nach 5 Sekunden ausblenden
    setTimeout(() => {
      resetEmailNotifier();
    }, 5000);
    
    // Wenn die E-Mail erfolgreich gesendet wurde, setze hasReplied auf true
    if (status === 'success') {
      setHasReplied(true);
    }
  };

  return (
    <div className={`inboxEmail ${expanded ? 'expanded' : ''} ${isRead ? 'read' : 'unread'}`}>
      <div className="world_email_header" onClick={toggleExpand}>
        <div className="world_email_summary">
          <p className="world_email_from">
            <strong>{sender.name}</strong>
          </p>
          {sender.email && <p className="world_email_address">{sender.email}</p>}
          <p className="world_email_subject">{email.subject}</p>
          
          <div className="world_email_date_container">
            <p className="world_email_time">{time}</p>
            <span>•</span>
            <p className="world_email_date">{date}</p>
            <span>•</span>
            <p className="world_email_account" title="E-Mail-Konto, das diese Nachricht empfangen hat">
              {receivedAtAccount}
            </p>
          </div>
        </div>
        
        <div className="world_email_expand_icon">
          <img 
            src="/img/down.svg" 
            alt={expanded ? "Einklappen" : "Ausklappen"} 
            className={expanded ? "rotated" : ""}
          />
        </div>
        
        {!isRead ? (
          <div className="world_email_status_container">
            <span className="world_email_unread_indicator" title="Unread"></span>
            <span className="world_email_status_text">Unread</span>
          </div>
        ) : (
          <div className="world_email_status_container">
            <span className="world_email_read_indicator" title="Read"></span>
            <span className="world_email_status_text">Read</span>
          </div>
        )}
        
        {leadUpdated && <span className="world_email_lead_updated" title="Kontakt wurde aktualisiert">✓</span>}
        
        <div className="world_email_buttons_container">
          {hasReplied && (
            <span 
              className="world_email_replied_badge" 
              title="Beantwortet am: ..."
            >
              ✓
            </span>
          )}
          
          <button 
            className="world_email_delete_button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(email);
            }}
            aria-label="E-Mail löschen"
          >
            <img src="/img/delete.svg" alt="Löschen" className="world_email_delete_icon" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="world_email_expanded_content">
          {/* Aktionsbuttons in der oberen rechten Ecke */}
          <div className="world_email_actions">
            <button 
              className="world_email_reply_button"
              onClick={(e) => {
                e.stopPropagation();
                handleReplyWithAi();
              }}
              disabled={!emailContent}
            >
              <img src="/img/mail.svg" alt="Reply" className="reply_icon" />
              Reply with AI
            </button>
          </div>
          
          {loading ? (
            <div className="email-loading-animation">
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              <span className="loading-dot"></span>
              <p className="loading-message"></p>
            </div>
          ) : error ? (
            <p className="world_email_error">{error}</p>
          ) : emailContent ? (
            <>
              <div className="world_email_body">
                {formatEmailContent(emailContent)}
              </div>
              
              {emailContent?.attachments && emailContent.attachments.length > 0 && (
                <div className="world_email_attachments">
                  <h4>Anhänge:</h4>
                  <div className="world_email_attachment_list">
                    {emailContent.attachments.map((attachment, index) => (
                      <div key={index} className="world_email_attachment_item">
                        <span className="world_email_attachment_icon">📎</span>
                        <span className="world_email_attachment_name">{attachment.filename}</span>
                        <span className="world_email_attachment_size">
                          ({Math.round(attachment.size / 1024)}KB)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <p>Keine Inhalte verfügbar</p>
          )}
        </div>
      )}
      
      {/* EmailAiPopup einbinden */}
      {showEmailAiPopup && emailReplyData && (
        <EmailAiPopup 
          onClose={(emailSent) => handleEmailPopupClose(emailSent)} 
          rowData={emailReplyData} 
          userId={userId}
          isReply={true}
          source="inbox"
          onEmailResult={handleEmailResult}
        />
      )}
      
      {/* E-Mail-Benachrichtigung am unteren Bildschirmrand */}
      {emailNotifier.status && (
        <EmailSentNotifier
          status={emailNotifier.status}
          message={emailNotifier.message}
          scheduledTime={emailNotifier.scheduledTime}
          recipientEmail={emailNotifier.recipientEmail}
          onClose={resetEmailNotifier}
        />
      )}
    </div>
  );
};

export default WorldInboxEmail;